import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import SeasonCompare from '@/components/help/widgets/SeasonCompare'
import { usePage } from '@inertiajs/react'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function SeasonsGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Seasons"
      subtitle="Capture predictable demand patterns -- summer holidays, Christmas, shoulder seasons."
    >
      <GuideSection title="Why Seasonal Pricing?">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Demand follows a yearly rhythm. Summer in a beach town is fundamentally different from
          January. A city-centre hotel sees a Christmas surge while a rural retreat may go quiet.
          Seasonal modifiers let you bake these known patterns into your rates automatically, so
          pricing adjusts without manual intervention every time the calendar turns.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Without seasonal pricing, you would need to manually adjust rates every time the season
          changes -- or worse, leave money on the table during peak periods and struggle to fill
          rooms in the off-season.
        </p>
      </GuideSection>

      <GuideSection title="How Seasons Work">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each season in {brand} has three components:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">A name</strong> -- something descriptive like
            "Summer Peak" or "Christmas & New Year"
          </li>
          <li>
            <strong className="text-foreground">A date range</strong> -- the start and end dates
            the season covers, inclusive on both ends
          </li>
          <li>
            <strong className="text-foreground">A percentage modifier</strong> -- applied to the
            base rate at stage 2 of the 13-stage pipeline
          </li>
        </ul>
        <div className="rounded-lg border bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Example:</strong> A "Summer" season from 1 June to
            31 August with <strong className="text-foreground">+20%</strong> means a £100 base rate
            becomes £120 during those months.
          </p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Multiple seasons can exist, but they should not overlap. If two seasons cover the same
          date, the most recently created one takes priority -- but this is best avoided by keeping
          date ranges clean.
        </p>
      </GuideSection>

      <GuideSection title="Setting Up Seasons">
        <p className="text-sm leading-relaxed text-muted-foreground">
          To create or edit seasons:
        </p>
        <ol className="ml-4 list-decimal space-y-2 text-sm text-muted-foreground">
          <li>
            Navigate to <strong className="text-foreground">Pricing Rules</strong> in the sidebar
          </li>
          <li>
            Select the <strong className="text-foreground">Seasons</strong> tab
          </li>
          <li>
            Click <strong className="text-foreground">Add Season</strong> and fill in the name,
            start/end dates, and modifier percentage
          </li>
          <li>
            Save -- the new season takes effect on the next rate computation
          </li>
        </ol>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Most properties start with 4-6 seasons covering the obvious demand patterns: peak summer,
          Christmas/New Year, shoulder months (spring and autumn), and an off-season baseline.
        </p>
      </GuideSection>

      <GuideWidget
        title="Season Impact"
        footnote="Shows how a +20% summer season affects rates with day-of-week modifiers already applied."
      >
        <SeasonCompare />
      </GuideWidget>

      <GuideSection title="Stacking with Other Stages">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Seasons stack multiplicatively with day-of-week modifiers. Each stage operates on the
          running total from the previous stage, not the original base rate. This is intentional
          -- it means the effects compound naturally.
        </p>
        <div className="rounded-lg border bg-muted/50 p-4 space-y-1">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Base rate:</strong> £100
          </p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Summer season (+20%):</strong> £100 × 1.20 = £120
          </p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Saturday (+15% of £120):</strong> £120 × 1.15 = £138
          </p>
          <p className="mt-2 text-sm font-medium text-foreground">
            Final rate before demand adjustments: £138
          </p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          If the season modifier were applied to the base rate independently, you would get £100 +
          £20 + £15 = £135. The multiplicative approach yields £138 instead -- the difference grows
          with larger modifiers, which is the correct behaviour for compounding price adjustments.
        </p>
      </GuideSection>

      <div className="space-y-3">
        <GuideTip type="do">
          Start with 4-6 seasons covering your obvious demand patterns: peak summer, Christmas/New
          Year, shoulder months, off-season. You can always add more later.
        </GuideTip>
        <GuideTip type="do">
          Use seasons for patterns that repeat yearly. For one-off demand spikes (a local festival,
          a sports event), use <strong className="text-foreground">Events</strong> instead -- they
          are designed for exactly that.
        </GuideTip>
        <GuideTip type="dont">
          Don't create dozens of micro-seasons with small modifiers. Keep it simple -- demand
          response handles the fine-grained daily adjustments based on real booking data.
        </GuideTip>
        <GuideTip type="dont">
          Don't overlap season dates. If two seasons cover the same date, behaviour may be
          unexpected. Use distinct, non-overlapping date ranges for predictable results.
        </GuideTip>
      </div>

      <GuideRelatedLinks
        links={[
          { slug: 'day-of-week', title: 'Day of Week' },
          { slug: 'events', title: 'Events' },
          { slug: 'how-it-works', title: 'How It Works' },
        ]}
      />
    </GuideLayout>
  )
}
